import axiosInstance from "../axiosInstance";
import { toast } from "react-hot-toast";
import { useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Card,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Form,
} from "react-bootstrap";
import { useEnv } from "../context/EnvContext";
import CopyableText from "../components/generic/CopyableText";
import { generateSampleCode } from "../components/generic/generateSampleCode";

const Registration = () => {
  const [debugEnv] = useState(null);
  const [selectedModel, setSelectedModel] = useState("llama-31-70b");
  const [isLoading, setIsLoading] = useState(true);
  const [tokenData, setTokenData] = useState({
    token: null,
    expiry: null,
    status: null,
  });

  // Use environment context
  const { env } = useEnv();

  // Auth config
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const gmid = activeAccount?.idTokenClaims.uid;

  // Environment configurations
  const envConfig = {
    dev: {
      baseUrl: {
        milford: "https://llmaas-gateway-dev.op-epg101im1.gm.com/api",
        warren: "https://llmaas-gateway-dev.op-epg101im1.gm.com/api",
      },
    },
    prod: {
      baseUrl: {
        milford: "https://llmaas-gateway-prod.op44im1.gm.com/api",
        warren: "https://llmaas-gateway-prod.op17wi.gm.com/api",
      },
    },
  };

  // Use debug environment if set, otherwise use real environment
  const effectiveEnv = debugEnv || env;
  const config = envConfig[effectiveEnv] || envConfig.dev;

  // Model configuration -- To be replaced with eureka logic eventually
  const modelConfig = useMemo(
    () => ({
      "llama-31-70b": {
        name: "LLama 31 70b",
        apiUrl: {
          milford: `${config.baseUrl.milford}/milford/llama31-70b`,
          warren: `${config.baseUrl.warren}/warren/llama31-70b`,
        },
        swaggerUrl: {
          dev: "https://llmaas-llama-31-70b-dev.op-epg101im1.gm.com/docs",
          prod: "https://llmaas-llama-31-70b.op44im1.gm.com/docs",
        },
        type: "TGI",
      },
      "deepseek-r1-70b": {
        name: "Deepseek R1 70b",
        apiUrl: {
          milford: `${config.baseUrl.milford}/milford/deepseek-r1-70b`,
          warren: `${config.baseUrl.warren}/warren/deepseek-r1-70b`,
        },
        swaggerUrl: {
          dev: "https://deepseek-r1-70b-dev.op-epg101im1.gm.com/docs",
          prod: "https://deepseek-r1-70b.op44im1.gm.com/docs",
        },
        type: "TGI",
      },
      "nomic-embed-text-v1.5": {
        name: "Nomic embed text v1.5",
        apiUrl: {
          milford: `${config.baseUrl.milford}/milford/nomic-embed-text-15`,
          warren: `${config.baseUrl.warren}/warren/nomic-embed-text-15`,
        },
        swaggerUrl: {
          dev: "https://llmaas-nomic-embed-text-15-dev.op-epg101im1.gm.com/docs",
          prod: "https://llmaas-nomic-embed-text-15.op44im1.gm.com/docs",
        },
        type: "TEI",
      },
      "bge-large-en-v1.5": {
        name: "bge large en v1.5",
        apiUrl: {
          milford: `${config.baseUrl.milford}/milford/bge-large-en-15`,
          warren: `${config.baseUrl.warren}/warren/bge-large-en-15`,
        },
        swaggerUrl: {
          dev: "https://llmaas-bge-large-en-15-dev.op-epg101im1.gm.com/docs",
          prod: "https://llmaas-bge-large-en-15.op44im1.gm.com/docs",
        },
        type: "TEI",
      },
      "bge-base-en-v1.5": {
        name: "bge base en v1.5",
        apiUrl: {
          milford: `${config.baseUrl.milford}/milford/bge-base-en-15`,
          warren: `${config.baseUrl.warren}/warren/bge-base-en-15`,
        },
        swaggerUrl: {
          dev: "https://llmaas-bge-base-en-15-dev.op-epg101im1.gm.com/docs",
          prod: "https://llmaas-bge-base-en-15.op44im1.gm.com/docs",
        },
        type: "TEI",
      },
      "gte-large-en-v1.5": {
        name: "gte large en v1.5",
        apiUrl: {
          milford: `${config.baseUrl.milford}/milford/gte-large-en-15`,
          warren: `${config.baseUrl.warren}/warren/gte-large-en-15`,
        },
        swaggerUrl: {
          dev: "https://llmaas-gte-large-en-15-dev.op-epg101im1.gm.com/docs",
          prod: "https://llmaas-gte-large-en-15.op44im1.gm.com/docs",
        },
        type: "TEI",
      },
    }),
    [config]
  );

  const sampleCode = useMemo(() => {
    // Check if token is populated before generating sample code
    if (!tokenData?.token) {
      return (
        <p className="text-center text-white-50 my-2">
          Please generate token to view sample code
        </p>
      );
    }
    return generateSampleCode(modelConfig, selectedModel, tokenData?.token);
  }, [selectedModel, modelConfig, tokenData?.token]);

  // Fetch token data on component mount
  useEffect(() => {
    const fetchTokenData = async () => {
      setIsLoading(true);

      if (!gmid) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await axiosInstance.get(`/retrieve-token/${gmid}`);
        const data = response.data;

        // If token exists, update state
        if (data && data.token) {
          // Check if token is expired
          const now = Math.floor(Date.now() / 1000);
          const isExpired = data.expiry < now;

          // Use stored status, override if token is expired
          const effectiveStatus = isExpired
            ? "expired"
            : data.status || "active";

          setTokenData({
            token: data.token,
            expiry: data.token_expiry_date,
            status: effectiveStatus,
          });
        } else {
          // No token found
          setTokenData({
            token: null,
            expiry: null,
            status: "no-token",
          });
        }
      } catch (error) {
        // Handle 404 response (Not token found for GMID)
        if (error.response && error.response.status === 404) {
          setTokenData({
            token: null,
            expiry: null,
            status: "no-token",
          });
        } else {
          console.error("Error fetching token data:", error);
          toast.error("Failed to retrieve token information");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchTokenData();
  }, [gmid]);

  const formatExpiryDate = (expiryEpoch) => {
    if (!expiryEpoch) return "No expiry date set";

    const expiryDate = new Date(expiryEpoch * 1000); // Convert epoch to milliseconds
    const now = new Date();

    // Format the date as "24 Sept 2025"
    const day = expiryDate.getDate();
    const month = expiryDate.toLocaleString("default", { month: "short" });
    const year = expiryDate.getFullYear();

    // Calculate remaining time
    const timeDifference = expiryDate - now;
    const daysRemaining = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const hoursRemaining = Math.floor(
      (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );

    return `${day} ${month} ${year} (${daysRemaining} Days, ${hoursRemaining} Hours)`;
  };

  const handleGenerateToken = async () => {
    // Set expiry date to 6 months from now
    const now = new Date();
    const expiryDate = new Date(now);

    const currentMonth = now.getMonth();
    expiryDate.setMonth(currentMonth + 6);

    const nowEpoch = Math.floor(now.getTime() / 1000);
    const expiryEpoch = Math.floor(expiryDate.getTime() / 1000);

    try {
      // Logic to generate a new token
      const response = await axiosInstance.post("/generate-token", {
        asms: "999999",
        gmid: gmid,
        roles: "llmaas",
        issueDate: nowEpoch,
        expiryDate: expiryEpoch,
      });
      // Return token data from response
      const newToken = response.data;
      toast.success("Token generated successfully!");

      setTokenData((prev) => ({
        ...prev,
        token: newToken,
        status: "active",
        expiry: expiryEpoch,
      }));
    } catch (error) {
      console.error("Error generating token:", error);
      toast.error("Failed to generate token. Please try again later.");
    }
  };

  const handleDeleteToken = async () => {
    // Update the token data in the database
    try {
      await axiosInstance.post(`/delete-token/${gmid}`);
      toast.success("Token deleted successfully!");
    } catch (error) {
      console.error("Error deleting token:", error);
      toast.error("Failed to delete token. Please try again later.");
    } finally {
      // Reset token data state
      setTokenData({
        token: null,
        expiry: null,
        status: "no-token",
      });
    }
  };

  const handleCopyToken = () => {
    const token = tokenData?.token ?? "";
    navigator.clipboard
      .writeText(token)
      .then(() => {
        toast.success("Token copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy token:", err);
        toast.error("Failed to copy token");
      });
  };

  const handleCopyCode = () => {
    navigator.clipboard
      .writeText(sampleCode)
      .then(() => {
        toast.success("Code copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy code:", err);
        toast.error("Failed to copy code");
      });
  };

  return (
    <>
      {/* {process.env.NODE_ENV === "development" && (
        <Container fluid className="bg-warning text-dark p-2 mb-2">
          <small>
            <strong>Debug Mode:</strong> Current env: {effectiveEnv}
            <Button
              size="sm"
              variant="outline-dark"
              className="ms-2"
              onClick={() => setDebugEnv(debugEnv === "prod" ? "dev" : "prod")}
            >
              Switch to {debugEnv === "prod" ? "dev" : "prod"}
            </Button>
            <Button
              size="sm"
              variant="outline-dark"
              className="ms-1"
              onClick={() => setDebugEnv(null)}
            >
              Use Real Env ({env})
            </Button>
          </small>
        </Container>
      )} */}

      <Container
        fluid
        className="title text-center py-2 mb-2"
        style={{
          backgroundColor: "#f8f9fa",
          borderBottom: "1px solid #dee2e6",
          marginTop: "0.5rem",
        }}
      >
        <h4 className="m-0">Self Registration Portal</h4>
      </Container>
      <Container
        fluid
        className="px-3"
        style={{ height: "calc(100vh - 200px)", overflow: "auto" }}
      >
        <Row className="justify-content-center g-3 h-100">
          <Col xs={12} md={8} lg={8} className="h-100">
            <div className="d-flex flex-column h-100">
              <Card className="mb-3" style={{ flex: "0 0 40%" }}>
                {isLoading && (
                  <div
                    className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{
                      zIndex: 10,
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      backdropFilter: "blur(4px)",
                      borderRadius: "inherit",
                    }}
                  >
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
                <Card.Body className="py-2">
                  <Card.Title className="text-center mb-2 pb-2 border-bottom">
                    Authentication
                  </Card.Title>
                  <Card.Text>
                    <Row>
                      <Col>
                        <Row>
                          <Col>
                            {/* Authentication Status */}
                            <Container className="authentication-status mb-2">
                              {/* Conditionally render status indicator */}
                              <span className="mb-1">
                                {(() => {
                                  switch (tokenData?.status) {
                                    case "active":
                                      return (
                                        <Badge bg="success" pill>
                                          Active
                                        </Badge>
                                      );
                                    case "expired":
                                      return (
                                        <Badge bg="danger" pill>
                                          Expired
                                        </Badge>
                                      );
                                    case "no-token":
                                      return (
                                        <Badge bg="secondary" pill>
                                          No Token
                                        </Badge>
                                      );
                                    default:
                                      return (
                                        <Badge bg="secondary" pill>
                                          No Token
                                        </Badge>
                                      );
                                  }
                                })()}
                              </span>
                            </Container>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            {/* Authentication Expiry info */}
                            <Container className="authentication-expiry">
                              {/* Retrive expiry info from DB */}
                              <p className="mb-1">Expiry Date</p>
                              <p className="mt-0">
                                {formatExpiryDate(tokenData?.expiry)}
                              </p>
                            </Container>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            {/* Authentication Info */}
                            <Container className="authentication-controls d-flex justify-content-center align-items-center">
                              <Button
                                variant="success"
                                onClick={handleGenerateToken}
                                disabled={tokenData?.status === "active"}
                                className="me-5"
                                style={{ width: "180px" }}
                              >
                                Generate New Token
                              </Button>
                              <Button
                                variant="danger"
                                onClick={handleDeleteToken}
                                disabled={tokenData?.status !== "active"}
                                style={{ width: "180px" }}
                              >
                                Delete Token
                              </Button>
                            </Container>
                          </Col>
                        </Row>
                      </Col>
                      <Col>
                        {/* Text area for Auth token */}
                        <Container className="authentication-token p-0">
                          <div className="token-display-container">
                            <div
                              className="token-title d-flex justify-content-between align-items-center p-1"
                              style={{ borderBottom: "1px solid #ccc" }}
                            >
                              <p className="m-0">Auth Token (JWT)</p>
                              {tokenData?.token && (
                                <i
                                  className="fas fa-copy me-3"
                                  style={{ cursor: "pointer" }}
                                  onClick={handleCopyToken}
                                  title="Copy token to clipboard"
                                ></i>
                              )}
                            </div>
                            <div
                              className="token-content p-3"
                              style={{
                                height: "135px",
                                maxHeight: "135px",
                                overflow: "auto",
                                backgroundColor: "#000",
                                color: "#fff",
                                borderBottomLeftRadius: "7px",
                                borderBottomRightRadius: "7px",
                                fontFamily: "Consolas, monospace",
                                fontSize: "0.8rem",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-all",
                              }}
                            >
                              {tokenData?.token ? (
                                tokenData.token
                              ) : (
                                <p className="text-center text-white-50 my-2">
                                  No token generated yet
                                </p>
                              )}
                            </div>
                          </div>
                        </Container>
                      </Col>
                    </Row>
                  </Card.Text>
                </Card.Body>
              </Card>

              <Card className="mb-3" style={{ flex: "0 0 30%" }}>
                <Card.Body className="py-2" style={{ overflow: "auto" }}>
                  <Card.Title className="text-center mb-2 pb-2 border-bottom">
                    Documentation
                  </Card.Title>
                  <Card.Text>
                    <Row>
                      <Col xs={12} md={2}>
                        {/* Documentation Links */}
                        <Container className="documentation-links">
                          <div className="mb-2">
                            <Button
                              variant="link"
                              className="p-0"
                              onClick={() =>
                                window.open("/LLMaaS_UserGuide.html", "_blank")
                              }
                            >
                              User Guide
                            </Button>
                          </div>
                        </Container>
                      </Col>
                      <Col xs={12} md={10}>
                        {/* Documentation Info */}
                        <Container className="documentation-info">
                          <h5>About LLMAAS</h5>
                          <p>
                            LLMAAS (Large Language Model as a Service) provides
                            API access to state-of-the-art language models for
                            enterprise applications. Our platform offers secure
                            token-based authentication, usage tracking, and
                            flexible integration options.
                          </p>
                          <p>
                            The service supports multiple language models
                            including LLama 31 70b and Deepseek R1 70b, with
                            customizable parameters for different use cases.
                            Users can generate text completions, embeddings, and
                            other AI-powered functionalities through our REST
                            API endpoints.
                          </p>
                          <p>
                            For more detailed information, please refer to our
                            comprehensive documentation and API reference guides
                            linked above.
                          </p>
                        </Container>
                      </Col>
                    </Row>
                  </Card.Text>
                </Card.Body>
              </Card>
            </div>
          </Col>
          <Col xs={12} md={4} lg={4} className="h-100">
            <Card className="h-100" style={{ marginBottom: "0" }}>
              <Card.Body
                className="py-2 d-flex flex-column"
                style={{ overflow: "hidden" }}
              >
                <Card.Title
                  className="text-center mb-2 pb-2 border-bottom"
                  style={{ flex: "0 0 auto" }}
                >
                  Technical Information
                </Card.Title>
                <div
                  style={{
                    flex: "0 0 auto",
                    overflow: "auto",
                    maxHeight: "40%",
                  }}
                >
                  <Container className="model-details">
                    <h5>Available Models</h5>
                    {/* <Form>
                      <div className="mb-3">
                        {Object.entries(modelConfig).map(([value, config]) => (
                          <Form.Check
                            key={value}
                            type="radio"
                            inline
                            id={`${value}-radio`}
                            label={config.name}
                            name="model"
                            value={value}
                            checked={selectedModel === value}
                            onChange={(e) => setSelectedModel(e.target.value)}
                          />
                        ))}
                      </div>
                    </Form> */}
                    <Form>
                      <div className="mb-3">
                        <Form.Select
                          value={selectedModel}
                          onChange={(e) => setSelectedModel(e.target.value)}
                          aria-label="Select model"
                        >
                          {Object.entries(modelConfig).map(
                            ([value, config]) => (
                              <option key={value} value={value}>
                                {config.name}
                              </option>
                            )
                          )}
                        </Form.Select>
                      </div>
                    </Form>

                    {/* Dynamic URLs based on selected model */}
                    <div className="mb-2">
                      <strong>API Endpoints:</strong>
                      <div className="ms-2">
                        <p className="mb-1">
                          <strong>Milford:</strong>{" "}
                          <CopyableText
                            text={modelConfig[selectedModel]?.apiUrl.milford}
                            label="Pod URL"
                            tooltipText="Click to copy"
                            style={{
                              color: "#007bff",
                              wordBreak: "break-word",
                              fontSize: ".9rem",
                            }}
                          />
                        </p>
                        <p className="mb-1">
                          <strong>Warren:</strong>{" "}
                          <CopyableText
                            text={modelConfig[selectedModel]?.apiUrl.warren}
                            label="Pod URL"
                            tooltipText="Click to copy"
                            style={{
                              color: "#007bff",
                              wordBreak: "break-word",
                              fontSize: ".9rem",
                            }}
                          />
                        </p>
                      </div>
                    </div>

                    <div className="mb-2">
                      <strong>Documentation:</strong>
                      <p className="mb-1">
                        <a
                          href={
                            modelConfig[selectedModel]?.swaggerUrl[env] ||
                            modelConfig[selectedModel]?.swaggerUrl
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none"
                        >
                          Swagger Documentation
                        </a>
                      </p>
                    </div>
                  </Container>
                </div>

                {/* Sample code section - fills remaining space */}
                <div
                  className="mt-3"
                  style={{
                    flex: "1",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <h5 style={{ flex: "0 0 auto" }}>Sample Python Code</h5>
                  <div
                    className="d-flex flex-column"
                    style={{ flex: "1", minHeight: 0 }}
                  >
                    <div
                      className="sample-code-title d-flex justify-content-between align-items-center p-1"
                      style={{
                        borderBottom: "1px solid #ccc",
                        flex: "0 0 auto",
                      }}
                    >
                      <p className="m-0">Python Example</p>
                      {tokenData?.token && (
                        <i
                          className="fas fa-copy me-3"
                          style={{ cursor: "pointer" }}
                          onClick={handleCopyCode}
                          title="Copy code to clipboard"
                        ></i>
                      )}
                    </div>
                    <div
                      className="sample-code-content p-3"
                      style={{
                        flex: "1",
                        overflow: "auto",
                        backgroundColor: "#000",
                        color: "#fff",
                        borderBottomLeftRadius: "7px",
                        borderBottomRightRadius: "7px",
                        fontFamily: "Consolas, monospace",
                        fontSize: "0.8rem",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        minHeight: 0,
                        maxHeight: "100%",
                      }}
                    >
                      {sampleCode}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Registration;
